<script setup>
import PageWrapper from '@/components/shared/page/PageWrapper.vue'
import PageSection from '@/components/shared/page/PageSection.vue'
import PageImage from '@/components/shared/page/PageImage.vue'
import Heading from '@/components/shared/page/Heading.vue'
import SideBySide from '@/components/shared/page/SideBySide.vue'
import PlainTable from '@/components/shared/table/PlainTable.vue'
import TableRow from '@/components/shared/table/TableRow.vue'
import TableHeaderCell from '@/components/shared/table/TableHeaderCell.vue'
import TableCell from '@/components/shared/table/TableCell.vue'
import AddressDisplay from '@/components/shared/transform/AddressDisplay.vue'
import DateTime from '@/components/shared/transform/DateTime.vue'
import Markdown from '@/components/shared/transform/Markdown.vue'
import FormField from '@/components/shared/form/FormField.vue'
import TimeInput from '@/components/shared/form/TimeInput.vue'
import TimezoneSelectInput from '@/components/shared/form/TimezoneSelectInput.vue'
import Timezone from '@/components/shared/transform/Timezone.vue'
import Spinner from '@/components/shared/core/Spinner.vue'
import ActionButton from '@/components/shared/buttons/ActionButton.vue'
import EnrollmentActions from '@/components/enrollments/EnrollmentActions.vue'
import EnrollmentBadge from '@/components/enrollments/EnrollmentBadge.vue'
import Modal from '@/components/shared/modals/Modal.vue'
import {myEnrollmentsApi} from '@/api/my-enrollments.api.js'
import {dateFormats} from '@/constants/date-and-time.js'
import {truncate} from '@/helpers/string.js'
import {useRouting} from '@/composables/use-routing.js'
import {useNavigation} from '@/composables/use-navigation.js'
import {useErrorHandling} from '@/composables/use-error-handling.js'
import {useCurrentOrganization} from '@/composables/use-current-organization.js'
import {useEnrollmentEditing} from '@/composables/use-enrollment-editing.js'
import {useEnrollmentManagement} from '@/composables/use-enrollment-management.js'
import {ref, computed, onMounted} from 'vue'

//Error handler and routing
const {processError} = useErrorHandling()
const {routeParams, replaceRoute} = useRouting()
const {enrollmentId} = routeParams.value

//Navigation
const {allowUsersToManageEnrollments} = useCurrentOrganization()
const {goToConversationForEnrollment} = useNavigation()

//Refs
const meta = ref(null)
const isLoadingEnrollment = ref(true)
const enrollment = ref(null)
const course = computed(() => enrollment.value?.course)

//Description truncation
const maxDescriptionLength = 170
const showFullDescription = ref(false)
const canShowFullDescription = computed(() => course.value?.description?.length > maxDescriptionLength)
const description = computed(() => {
  if (showFullDescription.value) {
    return course.value?.description
  }
  return truncate(course.value?.description, maxDescriptionLength)
})

//Load enrollment
const loadEnrollment = async () => {
  try {
    const data = await myEnrollmentsApi.findById(enrollmentId)
    enrollment.value = data.enrollment
    meta.value = data.meta
  }
  finally {
    isLoadingEnrollment.value = false
  }
}

//Enrollment editing
const {
  model,
  updateModel,
  updateEnrollment,
} = useEnrollmentEditing(enrollment)

//Enrollment management
const {
  resendLastMessage,
  isResendingLastMessage,
  startOrStopEnrollment,
  isStartingOrStoppingEnrollment,
} = useEnrollmentManagement(enrollment)

//View message details
const viewMessage = ref(null)
const setViewMessage = message => viewMessage.value = message
const clearViewMessage = () => viewMessage.value = null

//Load on mount
onMounted(async () => {
  if (!allowUsersToManageEnrollments.value) {
    replaceRoute('home')
    return
  }
  try {
    await loadEnrollment()
  }
  catch (error) {
    processError(error)
    replaceRoute('home')
  }
})
</script>
<template>
  <PageWrapper
    header="Manage enrollment"
    can-go-back
    @back="goToConversationForEnrollment(enrollment)"
  >
    <template #actions>
      <EnrollmentActions
        v-if="enrollment?.canPauseResume"
        can-toggle-paused
        :can-toggle-unread="!enrollment.hasEnded"
        :is-paused="!!enrollment.pausedAt"
        @toggle-paused="startOrStopEnrollment()"
      />
    </template>
    <Spinner v-if="isLoadingEnrollment" />

    <template v-else-if="enrollment && course">
      <PageSection header="Content">
        <Heading size="s">
          {{ enrollment.displayName }}
        </Heading>

        <PageImage
          :image-url="course.signedImageUrl"
          aria-label="Course image"
        />
        <Markdown :content="description" />

        <ActionButton
          v-if="canShowFullDescription && !showFullDescription"
          :class="$style.showMore"
          type="link"
          label="Show more"
          aria-label="Show full description"
          @click="showFullDescription = true"
        />
      </PageSection>

      <PageSection header="Enrollment details">
        <SideBySide>
          <FormField label="Delivery method">
            <AddressDisplay
              :address="enrollment.address"
              show-type
            />
          </FormField>
          <FormField
            v-if="!enrollment.isBinge"
            label="Schedule"
          >
            <p>{{ enrollment.deliveryDays }}</p>
          </FormField>
        </SideBySide>
        <SideBySide>
          <FormField label="Delivery time">
            <p v-if="enrollment.isBinge">
              Instant
            </p>
            <p v-else-if="!meta.isTimeChangeable">
              <DateTime
                :date="enrollment.expectedDeliveryTime"
                :format="dateFormats.DATE_AND_TIME"
              />
            </p>
            <TimeInput
              v-else
              :value="model.enrollmentTime"
              aria-label="Delivery time"
              @input="updateModel({enrollmentTime: $event.value})"
              @close="updateEnrollment()"
            />
          </FormField>
          <FormField label="Timezone">
            <TimezoneSelectInput
              v-if="meta.isTimezoneChangeable && !enrollment.isBinge && enrollment.isActive"
              :value="model.timezone"
              test-id="timezone"
              @input="updateModel({timezone: $event.value})"
              @dirty="updateEnrollment()"
            />
            <Timezone
              v-else
              :timezone="enrollment.effectiveTimezone"
              empty-label="Not selected"
            />
          </FormField>
        </SideBySide>

        <FormField>
          <EnrollmentBadge :enrollment="enrollment" />
        </FormField>

        <FormField>
          <ActionButton
            v-if="enrollment.canPauseResume"
            type="secondary"
            :label="enrollment.pausedAt ? 'Resume' : 'Pause'"
            :icon="enrollment.pausedAt ? 'play_arrow' : 'pause'"
            is-icon-outlined
            :is-disabled="!enrollment.isResumable || isStartingOrStoppingEnrollment"
            :has-spinner="isStartingOrStoppingEnrollment"
            @click="startOrStopEnrollment(enrollment)"
          />
        </FormField>
      </PageSection>

      <PageSection header="Messages">
        <template
          v-if="enrollment.canResend"
          #headerAside
        >
          <ActionButton
            type="secondary"
            label="Resend last message"
            :is-disabled="isResendingLastMessage"
            :has-spinner="isResendingLastMessage"
            @click="resendLastMessage()"
          />
        </template>
        <PlainTable
          v-if="meta.messages.length > 0"
          caption="Messages sent for this enrollment"
        >
          <template #head>
            <TableRow is-header>
              <TableHeaderCell :class="$style.type">
                Type
              </TableHeaderCell>
              <TableHeaderCell :class="$style.name">
                Step
              </TableHeaderCell>
              <TableHeaderCell :class="$style.content">
                Message body
              </TableHeaderCell>
              <TableHeaderCell :class="$style.date">
                Date
              </TableHeaderCell>
            </TableRow>
          </template>

          <TableRow
            v-for="message in meta.messages"
            :key="message.id"
            is-clickable
            :aria-label="`View full message for ${message.name}`"
            @click="setViewMessage(message)"
          >
            <TableCell :class="$style.type">
              {{ message.type }}
            </TableCell>
            <TableCell
              :class="$style.name"
              is-header-cell
            >
              {{ message.name }}
            </TableCell>
            <TableCell :class="$style.content">
              <Markdown
                :content="message.content"
                :max-length="100"
              />
            </TableCell>
            <TableCell :class="$style.date">
              <DateTime
                v-if="message.date"
                :format="dateFormats.DATE_AND_TIME"
                :date="message.date"
              />
              <span v-else>N/A</span>
            </TableCell>
          </TableRow>
        </PlainTable>

        <p v-else>
          No messages have been sent yet
        </p>
      </PageSection>
    </template>

    <Modal
      :is-open="!!viewMessage"
      :header="viewMessage?.name"
      close-on-key
      close-on-click-outside
      @close="clearViewMessage()"
    >
      <Markdown :content="viewMessage?.content" />
    </Modal>
  </PageWrapper>
</template>

<style module lang="scss">
.image {
  background-position: center center;
  height: 350px;
  background-repeat: no-repeat;
  background-size: contain;
  border-radius: var(--border-radius-m);
}
.section {
  &.half {
    width: 50%;
  }
  &:not(:first-child) {
    margin-top: 2rem;
  }
  p {
    margin-top: 0;
  }
}
.progress {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.name {
  width: 20%;
  min-width: 8rem;
}
.type {
  display: none;
  width: 6rem;
  min-width: 5rem;
  @media (min-width: 768px) {
    display: table-cell;
  }
}
.date {
  display: none;
  width: 12rem;
  min-width: 10rem;
  @media (min-width: 600px) {
    display: table-cell;
  }
}
.content {
  display: none;
  width: auto; // Takes remaining space
  min-width: 15rem;
  @media (min-width: 1240px) {
    display: table-cell;
  }
}
.showMore {
  margin-top: 0.5rem;
}
</style>
